import { z } from "zod";

// Browser-direct OpenFoodFacts client (replaces the retired Convex action
// path). OFF rate limits apply per IP; calling from each user's browser keeps
// the quota per user instead of sharing one Convex egress IP across everyone.
// Production evidence: world.openfoodfacts.org answers the OPTIONS preflight
// (triggered by a custom X-User-Agent header) with 503, so browsers block the
// actual GET. OFF_HEADERS therefore stays CORS-safelisted (Accept only) —
// every request is a preflight-free "simple" request and the browser's own
// User-Agent, sent automatically, satisfies OFF's identification expectation.

const OFF_BASE_URL = "https://world.openfoodfacts.org";
const OFF_HEADERS: Record<string, string> = {
  Accept: "application/json",
};
const OFF_TIMEOUT_MS = 10_000;
// image_front_url is the full-size front-of-pack photo (the card cover);
// image_url stays projected as the fallback for products whose contributors
// only uploaded a "main" image.
const PRODUCT_FIELDS =
  "code,product_name,brands,serving_size,nutriments,image_url,image_front_url,no_nutrition_data";
const BARCODE_PATTERN = /^\d{8,14}$/;
const SEARCH_TTL_MS = 5 * 60 * 1000;
const CACHE_CAP = 50;

const LOG_PREFIX = "[Frontend][OpenFoodFacts]";

// ── Typed errors ────────────────────────────────────────────────────────────

export class OffUnavailableError extends Error {
  readonly code = "OFF_UNAVAILABLE";
  constructor() {
    super("OpenFoodFacts is temporarily unavailable.");
    this.name = "OffUnavailableError";
  }
}

export class OffValidationError extends Error {
  readonly code = "VALIDATION";
  constructor() {
    super("Barcode must contain 8 to 14 digits.");
    this.name = "OffValidationError";
  }
}

// A request superseded by a newer search rejects with this; callers swallow
// it without surfacing an error state.
export class OffAbortError extends Error {
  constructor() {
    super("OpenFoodFacts request was superseded by a newer search.");
    this.name = "OffAbortError";
  }
}

export function isOffAbortError(error: unknown): boolean {
  return (
    error instanceof OffAbortError ||
    (error instanceof DOMException && error.name === "AbortError")
  );
}

// ── Schemas + mapping (ported from the retired convex/openfoodfacts.ts) ─────

const offProductSchema = z.object({
  code: z.union([z.string(), z.number()]).optional(),
  product_name: z.string().optional(),
  brands: z.string().optional(),
  serving_size: z.string().optional(),
  nutriments: z.record(z.string(), z.unknown()).optional(),
  image_url: z.string().optional(),
  image_front_url: z.string().optional(),
  no_nutrition_data: z.string().optional(),
});

const offLookupSchema = z.object({
  status: z.number().optional(),
  product: offProductSchema.optional(),
});

const offSearchSchema = z.object({
  products: z.array(offProductSchema).optional(),
});

export type OffNutritionValues = Readonly<{
  calories?: number;
  proteinG?: number;
  carbsG?: number;
  fatG?: number;
  fiberG?: number;
}>;

export type MappedOffProduct = {
  readonly barcode?: string;
  readonly name?: string;
  readonly brands?: string;
  readonly servingSize?: string;
  readonly imageUrl?: string;
  readonly imageFrontUrl?: string;
  readonly per100g: OffNutritionValues;
  readonly perServing: OffNutritionValues;
  readonly incomplete: boolean;
  readonly noNutritionData: boolean;
  readonly hasBarcode: boolean;
};

export type OffLookupResult =
  | { readonly found: false }
  | { readonly found: true; readonly product: MappedOffProduct };

function optionalNumber(nutriments: Readonly<Record<string, unknown>>, key: string) {
  const value = nutriments[key];
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function optionalString(value: string | undefined) {
  return value === undefined || value.trim() === "" ? undefined : value;
}

function mapNutrition(
  nutriments: Readonly<Record<string, unknown>>,
  suffix: "100g" | "serving",
): OffNutritionValues {
  const calories = optionalNumber(nutriments, `energy-kcal_${suffix}`);
  const proteinG = optionalNumber(nutriments, `proteins_${suffix}`);
  const carbsG = optionalNumber(nutriments, `carbohydrates_${suffix}`);
  const fatG = optionalNumber(nutriments, `fat_${suffix}`);
  const fiberG = optionalNumber(nutriments, `fiber_${suffix}`);

  return {
    ...(calories === undefined ? {} : { calories }),
    ...(proteinG === undefined ? {} : { proteinG }),
    ...(carbsG === undefined ? {} : { carbsG }),
    ...(fatG === undefined ? {} : { fatG }),
    ...(fiberG === undefined ? {} : { fiberG }),
  };
}

function mapProduct(product: z.infer<typeof offProductSchema>): MappedOffProduct {
  const nutriments = product.nutriments ?? {};
  const barcodeValue = product.code === undefined ? undefined : String(product.code);
  const barcode = optionalString(barcodeValue);
  const name = optionalString(product.product_name);
  const brands = optionalString(product.brands);
  const servingSize = optionalString(product.serving_size);
  const imageUrl = optionalString(product.image_url);
  const imageFrontUrl = optionalString(product.image_front_url);
  const per100g = mapNutrition(nutriments, "100g");

  return {
    ...(barcode === undefined ? {} : { barcode }),
    ...(name === undefined ? {} : { name }),
    ...(brands === undefined ? {} : { brands }),
    ...(servingSize === undefined ? {} : { servingSize }),
    ...(imageUrl === undefined ? {} : { imageUrl }),
    ...(imageFrontUrl === undefined ? {} : { imageFrontUrl }),
    per100g,
    perServing: mapNutrition(nutriments, "serving"),
    incomplete:
      per100g.calories === undefined ||
      per100g.proteinG === undefined ||
      per100g.carbsG === undefined ||
      per100g.fatG === undefined ||
      per100g.fiberG === undefined,
    noNutritionData: product.no_nutrition_data === "on",
    hasBarcode: barcode !== undefined,
  };
}

export function mapOffProduct(json: unknown): OffLookupResult {
  const parsed = offLookupSchema.safeParse(json);
  if (!parsed.success || parsed.data.status !== 1 || parsed.data.product === undefined) {
    return { found: false };
  }
  return { found: true, product: mapProduct(parsed.data.product) };
}

// Display image for any OFF-shaped product (the barcode/search screens keep
// structural mirrors with these two optional fields): prefer the full-size
// front-of-pack photo, fall back to the main image, else undefined and the
// UI renders its no-photo placeholder.
export function offProductImageUrl(product: {
  readonly imageUrl?: string;
  readonly imageFrontUrl?: string;
}): string | undefined {
  return product.imageFrontUrl ?? product.imageUrl;
}

// OFF content is CC BY-SA; every surface showing OFF data/images carries it.
export const OFF_ATTRIBUTION = "Product data and images from Open Food Facts.";

// ── In-memory caches (search: 5 min TTL; barcode: session) ─────────────────

interface SearchCacheEntry {
  readonly expiresAt: number;
  readonly results: readonly MappedOffProduct[];
}

const searchCache = new Map<string, SearchCacheEntry>();
const barcodeCache = new Map<string, OffLookupResult>();

export function normalizeOffSearchTerm(query: string): string {
  return query.trim().toLowerCase().replace(/\s+/g, " ");
}

function cacheSet<K, V>(cache: Map<K, V>, key: K, value: V): void {
  if (cache.size >= CACHE_CAP && !cache.has(key)) {
    const oldest = cache.keys().next();
    if (!oldest.done) cache.delete(oldest.value);
  }
  cache.set(key, value);
}

// Test hook: vitest suites reset module-level caches between cases.
export function clearOffCachesForTests(): void {
  searchCache.clear();
  barcodeCache.clear();
}

// OFF's search endpoints return intermittent 503s under load, so transient
// failures get multiple attempts with escalating jittered backoff.
const OFF_MAX_ATTEMPTS = 3;

// ── Fetch with timeout composition + bounded retries ────────────────────────

function isAbortException(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === "AbortError") ||
    (error instanceof Error && error.name === "AbortError")
  );
}

function jitteredBackoff(failedAttempt: number): Promise<void> {
  const baseMs = failedAttempt <= 1 ? 1000 : 2500;
  const delayMs = baseMs + Math.random() * 1500;
  return new Promise((resolve) => setTimeout(resolve, delayMs));
}

// One attempt with a 10s timeout composed onto the caller's signal (manual
// relay: the ES2020 lib has no AbortSignal.any). Rejects OffAbortError when
// the CALLER aborted; all other failures reject OffUnavailableError.
async function fetchOffOnce(url: string, callerSignal: AbortSignal | undefined): Promise<Response> {
  const controller = new AbortController();
  let timedOut = false;
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, OFF_TIMEOUT_MS);
  const relayCallerAbort = () => controller.abort();
  if (callerSignal !== undefined) {
    if (callerSignal.aborted) {
      clearTimeout(timeout);
      throw new OffAbortError();
    }
    callerSignal.addEventListener("abort", relayCallerAbort, { once: true });
  }
  try {
    const response = await fetch(url, { headers: OFF_HEADERS, signal: controller.signal });
    if (!response.ok && response.status !== 404) {
      // 429/5xx are retryable; other 4xx are terminal for this request shape.
      const retryable = response.status === 429 || response.status >= 500;
      throw Object.assign(new OffUnavailableError(), { retryable });
    }
    return response;
  } catch (error: unknown) {
    if (callerSignal?.aborted === true) {
      throw new OffAbortError();
    }
    if (isAbortException(error)) {
      // Abort without the caller's signal means OUR timeout fired (or the
      // environment lacks mediaDevices-style abort reasons) — retryable.
      if (timedOut) {
        throw Object.assign(new OffUnavailableError(), { retryable: true });
      }
      throw new OffAbortError();
    }
    if (error instanceof OffUnavailableError) {
      throw error;
    }
    // Network TypeError (DNS/offline/CORS) — retryable once.
    throw Object.assign(new OffUnavailableError(), { retryable: true });
  } finally {
    clearTimeout(timeout);
    callerSignal?.removeEventListener("abort", relayCallerAbort);
  }
}

async function fetchOff(url: string, callerSignal: AbortSignal | undefined): Promise<Response> {
  const callerAborted = () => callerSignal?.aborted === true;
  for (let attempt = 1; attempt <= OFF_MAX_ATTEMPTS; attempt++) {
    try {
      return await fetchOffOnce(url, callerSignal);
    } catch (error: unknown) {
      if (error instanceof OffAbortError) throw error;
      const retryable =
        error instanceof OffUnavailableError &&
        (error as OffUnavailableError & { retryable?: boolean }).retryable === true;
      if (!retryable || attempt === OFF_MAX_ATTEMPTS || callerAborted()) {
        throw new OffUnavailableError();
      }
      console.log(`${LOG_PREFIX} Retrying request after a transient failure`, {
        url,
        nextAttempt: attempt + 1,
        maxAttempts: OFF_MAX_ATTEMPTS,
      });
      await jitteredBackoff(attempt);
      if (callerAborted()) throw new OffAbortError();
    }
  }
  throw new OffUnavailableError();
}

async function parseJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    throw new OffUnavailableError();
  }
}

// ── Public API ──────────────────────────────────────────────────────────────

export async function lookupOffBarcode(
  barcode: string,
  signal?: AbortSignal,
): Promise<OffLookupResult> {
  const normalizedBarcode = barcode.trim();
  if (!BARCODE_PATTERN.test(normalizedBarcode)) {
    console.warn(`${LOG_PREFIX} Barcode rejected by validation`, { digits: normalizedBarcode.length });
    throw new OffValidationError();
  }

  const cached = barcodeCache.get(normalizedBarcode);
  if (cached !== undefined) {
    console.log(`${LOG_PREFIX} Barcode cache hit`, { barcode: normalizedBarcode, found: cached.found });
    return cached;
  }

  console.log(`${LOG_PREFIX} Barcode lookup started`, { barcode: normalizedBarcode });
  const response = await fetchOff(
    `${OFF_BASE_URL}/api/v2/product/${normalizedBarcode}.json?fields=${PRODUCT_FIELDS}`,
    signal,
  );
  const result: OffLookupResult =
    response.status === 404 ? { found: false } : mapOffProduct(await parseJson(response));
  cacheSet(barcodeCache, normalizedBarcode, result);
  console.log(`${LOG_PREFIX} Barcode lookup completed`, {
    barcode: normalizedBarcode,
    found: result.found,
  });
  return result;
}

export async function searchOffProducts(
  query: string,
  page = 1,
  signal?: AbortSignal,
): Promise<readonly MappedOffProduct[]> {
  const term = normalizeOffSearchTerm(query);
  if (term === "") {
    console.warn(`${LOG_PREFIX} Search skipped: empty query`);
    return [];
  }

  const cached = searchCache.get(term);
  if (cached !== undefined && cached.expiresAt > Date.now()) {
    console.log(`${LOG_PREFIX} Search cache hit`, { query: term, resultCount: cached.results.length });
    return cached.results;
  }

  const params = new URLSearchParams({
    search_terms: term,
    json: "1",
    page_size: "20",
    fields: PRODUCT_FIELDS,
  });
  if (page !== 1) {
    params.set("page", String(page));
  }

  console.log(`${LOG_PREFIX} Product search started`, { query: term, page });
  const response = await fetchOff(`${OFF_BASE_URL}/cgi/search.pl?${params.toString()}`, signal);
  const json = await parseJson(response);
  const parsed = offSearchSchema.safeParse(json);
  if (!parsed.success) {
    console.error(`${LOG_PREFIX} Search response failed schema validation`, { query: term });
    throw new OffUnavailableError();
  }
  const results = (parsed.data.products ?? []).map(mapProduct);
  cacheSet(searchCache, term, { expiresAt: Date.now() + SEARCH_TTL_MS, results });
  console.log(`${LOG_PREFIX} Product search completed`, { query: term, resultCount: results.length });
  return results;
}
